import { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  MessageSquare,
  Brain,
  ScrollText,
  Search,
  Trash2,
  LogOut,
  Sparkles,
  Upload,
  Bot,
  Plug,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function groupByDate(conversas) {
  const groups = { Hoje: [], Ontem: [], "Últimos 7 dias": [], Anteriores: [] };
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86400000;
  conversas.forEach((c) => {
    const t = new Date(c.atualizada_em || c.criada_em).getTime();
    if (t >= startToday) groups["Hoje"].push(c);
    else if (t >= startToday - dayMs) groups["Ontem"].push(c);
    else if (t >= startToday - 7 * dayMs) groups["Últimos 7 dias"].push(c);
    else groups["Anteriores"].push(c);
  });
  return groups;
}

export default function Sidebar({ conversas, activeId, reloadConversas }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const filtered = conversas.filter((c) =>
    (c.titulo || "").toLowerCase().includes(query.toLowerCase())
  );
  const groups = groupByDate(filtered);

  const newChat = () => {
    // A conversa só é criada na primeira mensagem (evita conversas vazias e corridas).
    navigate("/");
  };

  const remove = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    await api.delete(`/conversas/${id}`);
    await reloadConversas();
    if (activeId === id) navigate("/");
    toast.success("Conversa apagada");
  };

  const navItem = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors";

  return (
    <aside className="w-72 h-full bg-slate-950/95 border-r border-slate-800/80 flex flex-col sm-grain">
      {/* Brand */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <Brain size={18} className="text-emerald-400" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-bold text-[15px] tracking-tight">Super Memória</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400/80">
            memória partilhada
          </div>
        </div>
      </div>

      <div className="px-3">
        <button
          data-testid="sidebar-new-chat-button"
          onClick={newChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/20"
        >
          <Plus size={16} strokeWidth={2.5} /> Nova conversa
        </button>
      </div>

      {/* Search */}
      <div className="px-3 mt-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="sidebar-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Procurar conversas"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-900/70 border border-slate-800 text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
          />
        </div>
      </div>

      {/* Conversa list */}
      <div className="flex-1 overflow-y-auto px-3 mt-3 space-y-4">
        {Object.entries(groups).map(([label, items]) =>
          items.length ? (
            <div key={label}>
              <div className="px-1 mb-1 text-[10px] font-mono uppercase tracking-widest text-slate-600">
                {label}
              </div>
              <div className="space-y-0.5">
                {items.map((c) => (
                  <NavLink
                    key={c.id}
                    to={`/c/${c.id}`}
                    data-testid="sidebar-chat-item"
                    className={({ isActive }) =>
                      `${navItem} group ${
                        isActive
                          ? "bg-emerald-500/10 border border-emerald-500/25 text-slate-100"
                          : "text-slate-400 hover:bg-slate-800/50 border border-transparent"
                      }`
                    }
                  >
                    <MessageSquare size={15} className="shrink-0 opacity-70" />
                    <span className="truncate flex-1">{c.titulo || "Nova conversa"}</span>
                    <button
                      onClick={(e) => remove(e, c.id)}
                      data-testid="sidebar-chat-delete"
                      className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </NavLink>
                ))}
              </div>
            </div>
          ) : null
        )}
        {filtered.length === 0 && (
          <div className="text-center text-slate-600 text-xs mt-8 font-mono">sem conversas</div>
        )}
      </div>

      {/* Nav */}
      <div className="px-3 py-2 border-t border-slate-800/80 space-y-0.5">
        <NavLink
          to="/agentes"
          data-testid="sidebar-nav-agentes"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <Bot size={16} /> Agentes
        </NavLink>
        <NavLink
          to="/cerebro"
          data-testid="sidebar-nav-cerebro"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <Brain size={16} /> Cérebro
        </NavLink>
        <NavLink
          to="/fontes"
          data-testid="sidebar-nav-fontes"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <Upload size={16} /> Fontes
        </NavLink>
        <NavLink
          to="/memory"
          data-testid="sidebar-nav-memory"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <Sparkles size={16} /> Memória / Factos
        </NavLink>
        <NavLink
          to="/rules"
          data-testid="sidebar-nav-rules"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <ScrollText size={16} /> Regras permanentes
        </NavLink>
        <NavLink
          to="/apps"
          data-testid="sidebar-nav-apps"
          className={({ isActive }) =>
            `${navItem} ${isActive ? "text-emerald-400 bg-emerald-500/5" : "text-slate-400 hover:bg-slate-800/50"}`
          }
        >
          <Plug size={16} /> Apps ligadas
        </NavLink>
      </div>

      {/* User footer */}
      <div className="px-3 py-3 border-t border-slate-800/80 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-semibold text-emerald-400">
          {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{user?.name || "Utilizador"}</div>
          <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="text-slate-500 hover:text-red-400 transition-colors"
          title="Terminar sessão"
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
